/**
 * Queries, keys, and mutations for templates and partials (AREA-TEMPLATES).
 *
 * Keys reuse the shared `queryKeys.templates` root so the command palette and
 * pickers refresh when anything here invalidates `queryKeys.templates.all`.
 * Partials and preview data get their own roots.
 */
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, unwrap } from '@/api/client'
import { asApiError } from '@/api/errors'
import { fetchAll } from '@/api/fetch-all'
import { queryKeys } from '@/api/keys'
import { useInfiniteList } from '@/api/pagination'
import { proposalKeys } from '@/api/queries/proposals'
import {
  MAX_PAGE_SIZE,
  type CreatePartial,
  type PartialDetail,
  type CreateTemplate,
  type PublishTemplate,
  type RenderTemplateRequest,
  type SaveDraft,
  type TemplateDetail,
  type TemplateFormat,
  type TestSendRequest,
  type UpdatePartial,
  type UpdateTemplate,
} from '@/api/types'

/** Owner view on list pages: everything in scope, only the scoped app, or the shared library. */
export type OwnerView = 'all' | 'app' | 'shared'

export interface TemplateListFilters {
  /** Application ID, 'shared' for the shared library, or undefined for all. */
  app?: string
  /** With an application: also list shared templates. */
  includeShared?: boolean
  q?: string
  format?: TemplateFormat
}

/**
 * API filters for an owner view under the global app scope. With an app in
 * scope, "all" means that app plus the shared library.
 */
export function ownerFilters(view: OwnerView, appId: string | undefined): TemplateListFilters {
  if (view === 'shared') return { app: 'shared' }
  if (!appId) return {}
  return view === 'app' ? { app: appId } : { app: appId, includeShared: true }
}

export const templateKeys = {
  all: queryKeys.templates.all,
  // `view: 'table'` keeps these infinite-query keys apart from the palette's
  // plain list queries, which share the ['templates', 'list', …] prefix.
  list: (f: TemplateListFilters) => [...queryKeys.templates.all, 'list', { view: 'table', ...f }],
  detail: (id: string) => queryKeys.templates.detail(id),
  versions: (id: string) => [...queryKeys.templates.detail(id), 'versions'] as const,
  version: (id: string, vid: string) =>
    [...queryKeys.templates.detail(id), 'versions', vid] as const,
  render: (id: string, body: unknown) => [...queryKeys.templates.all, 'render', id, body] as const,
}

export const partialKeys = {
  all: ['partials'] as const,
  list: (f: { app?: string; includeShared?: boolean; q?: string }) =>
    ['partials', 'list', f] as const,
  /** Every partial key visible to an owner, for `{{>` completion. */
  keys: (app: string | null | undefined) => ['partials', 'keys', app ?? 'shared'] as const,
  detail: (id: string) => ['partials', 'detail', id] as const,
}

export const previewKeys = {
  eventType: (id: string) => queryKeys.eventTypes.detail(id),
  context: (id: string, app?: string | null) =>
    [...queryKeys.eventTypes.detail(id), 'context', app ?? null] as const,
  eventTypeOptions: (app: string | null | undefined) =>
    [...queryKeys.eventTypes.all, 'list', { view: 'template-picker', app: app ?? null }] as const,
}

/** True for responses that mean "this endpoint isn't built yet". */
export function isNotImplemented(err: unknown): boolean {
  if (err == null) return false
  const e = asApiError(err)
  return e.status === 501 || e.code === 'not_implemented'
}

/** Retry like the default (network and 5xx), but never a 501. */
function retryUnlessNotImplemented(failureCount: number, err: unknown): boolean {
  if (isNotImplemented(err)) return false
  const e = asApiError(err)
  return (e.status === 0 || e.status >= 500) && failureCount < 2
}

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

export function useTemplatesList(f: TemplateListFilters) {
  return useInfiniteList({
    queryKey: templateKeys.list(f),
    fetchPage: (cursor) =>
      unwrap(
        api.GET('/admin/v1/templates', {
          params: {
            query: {
              app: f.app,
              include_shared: f.app && f.app !== 'shared' ? f.includeShared : undefined,
              q: f.q || undefined,
              format: f.format,
              limit: 100,
              cursor,
            },
          },
        }),
      ),
  })
}

export const templateQuery = (id: string) =>
  queryOptions({
    queryKey: templateKeys.detail(id),
    queryFn: () => unwrap(api.GET('/admin/v1/templates/{id}', { params: { path: { id } } })),
  })

export const versionsQuery = (id: string) =>
  queryOptions({
    queryKey: templateKeys.versions(id),
    queryFn: () =>
      fetchAll(
        (cursor) =>
          unwrap(
            api.GET('/admin/v1/templates/{id}/versions', {
              params: { path: { id }, query: { limit: MAX_PAGE_SIZE, cursor } },
            }),
          ),
        1000,
      ),
  })

export const versionQuery = (id: string, vid: string) =>
  queryOptions({
    queryKey: templateKeys.version(id, vid),
    queryFn: () =>
      unwrap(api.GET('/admin/v1/templates/{id}/versions/{vid}', { params: { path: { id, vid } } })),
    // Published versions never change.
    staleTime: Infinity,
  })

export function useCreateTemplate() {
  return useMutation({
    mutationFn: (body: CreateTemplate) => unwrap(api.POST('/admin/v1/templates', { body })),
    meta: {
      invalidates: [queryKeys.templates.all, queryKeys.applications.all],
      successMessage: 'Template created',
      errorToast: false,
    },
  })
}

/** Writes the returned detail into the cache so the studio updates without a refetch. */
function useDetailWriter() {
  const qc = useQueryClient()
  return (detail: TemplateDetail) => qc.setQueryData(templateKeys.detail(detail.id), detail)
}

export function useUpdateTemplate(id: string) {
  const write = useDetailWriter()
  return useMutation({
    mutationFn: (body: UpdateTemplate) =>
      unwrap(api.PATCH('/admin/v1/templates/{id}', { params: { path: { id } }, body })),
    onSuccess: write,
    meta: { invalidates: [[...queryKeys.templates.all, 'list']] },
  })
}

export function useSaveDraft(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: SaveDraft) =>
      unwrap(api.PUT('/admin/v1/templates/{id}/draft', { params: { path: { id } }, body })),
    onSuccess: (draft) => {
      qc.setQueryData<TemplateDetail>(templateKeys.detail(id), (old) =>
        old ? { ...old, draft, updated_at: draft.updated_at, updated_by: draft.updated_by } : old,
      )
    },
    meta: { invalidates: [[...queryKeys.templates.all, 'list']], errorToast: false },
  })
}

export function useDiscardDraft(id: string) {
  return useMutation({
    mutationFn: () =>
      unwrap(api.DELETE('/admin/v1/templates/{id}/draft', { params: { path: { id } } })),
    meta: { invalidates: [queryKeys.templates.all], successMessage: 'Draft discarded' },
  })
}

export function usePublishTemplate(id: string) {
  const write = useDetailWriter()
  return useMutation({
    mutationFn: (body: PublishTemplate) =>
      unwrap(api.POST('/admin/v1/templates/{id}/publish', { params: { path: { id } }, body })),
    onSuccess: write,
    meta: { invalidates: [queryKeys.templates.all], errorToast: false },
  })
}

export function useActivateVersion(id: string) {
  const write = useDetailWriter()
  return useMutation({
    mutationFn: (vid: string) =>
      unwrap(
        api.POST('/admin/v1/templates/{id}/versions/{vid}/activate', {
          params: { path: { id, vid } },
        }),
      ),
    onSuccess: write,
    meta: { invalidates: [queryKeys.templates.all] },
  })
}

/**
 * Delete a template. On success only list queries refresh: the deleted
 * template's detail, versions and renders are still mounted, and refetching
 * them would 404, toast and flash a "not found" screen. Navigate away first,
 * then call `forget(id)` to drop them.
 */
export function useDeleteTemplate() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE('/admin/v1/templates/{id}', { params: { path: { id } } })),
    meta: {
      invalidates: [[...queryKeys.templates.all, 'list'], queryKeys.applications.all],
      successMessage: 'Template deleted',
    },
  })
  const forget = (id: string) => {
    qc.removeQueries({ queryKey: templateKeys.detail(id) })
    qc.removeQueries({ queryKey: [...queryKeys.templates.all, 'render', id] })
    return qc.invalidateQueries({ queryKey: queryKeys.templates.all })
  }
  return { ...mutation, forget }
}

export function renderTemplate(id: string, body: RenderTemplateRequest, signal?: AbortSignal) {
  return unwrap(
    api.POST('/admin/v1/templates/{id}/render', { params: { path: { id } }, body, signal }),
  )
}

export function useTestSend(id: string) {
  return useMutation({
    mutationFn: (body: TestSendRequest) =>
      unwrap(api.POST('/admin/v1/templates/{id}/test-send', { params: { path: { id } }, body })),
    meta: { errorToast: false },
  })
}

// ---------------------------------------------------------------------------
// Preview data: event types, samples, and the completion context
// ---------------------------------------------------------------------------

/** Event types a template can preview against: its app's, or every app's for shared templates. */
export const eventTypeOptionsQuery = (app: string | null | undefined) =>
  queryOptions({
    queryKey: previewKeys.eventTypeOptions(app),
    queryFn: () =>
      fetchAll((cursor) =>
        unwrap(
          api.GET('/admin/v1/event-types', {
            params: { query: { app: app ?? undefined, limit: MAX_PAGE_SIZE, cursor } },
          }),
        ),
      ),
    staleTime: 60_000,
  })

export const eventTypeQuery = (id: string) =>
  queryOptions({
    queryKey: previewKeys.eventType(id),
    queryFn: () => unwrap(api.GET('/admin/v1/event-types/{id}', { params: { path: { id } } })),
  })

export const contextQuery = (eventTypeId: string, applicationId?: string | null) =>
  queryOptions({
    queryKey: previewKeys.context(eventTypeId, applicationId),
    queryFn: () =>
      unwrap(
        api.GET('/admin/v1/event-types/{id}/context', {
          params: {
            path: { id: eventTypeId },
            query: { application_id: applicationId ?? undefined },
          },
        }),
      ),
    retry: retryUnlessNotImplemented,
    staleTime: 60_000,
  })

// ---------------------------------------------------------------------------
// Partials
// ---------------------------------------------------------------------------

export function fetchPartialsPage(
  f: { app?: string; includeShared?: boolean; q?: string },
  cursor?: string,
  limit = 100,
) {
  return unwrap(
    api.GET('/admin/v1/partials', {
      params: {
        query: {
          app: f.app,
          include_shared: f.app && f.app !== 'shared' ? f.includeShared : undefined,
          q: f.q || undefined,
          limit,
          cursor,
        },
      },
    }),
  )
}

export function usePartialsList(f: { app?: string; includeShared?: boolean; q?: string }) {
  return useInfiniteList({
    queryKey: partialKeys.list(f),
    fetchPage: (cursor) => fetchPartialsPage(f, cursor),
  })
}

/** Every partial visible to a template owner (app + shared, or shared only). */
export const visiblePartialsQuery = (applicationId: string | null | undefined) =>
  queryOptions({
    queryKey: partialKeys.keys(applicationId),
    queryFn: () =>
      fetchAll((cursor) =>
        fetchPartialsPage(
          { app: applicationId ?? 'shared', includeShared: true },
          cursor,
          MAX_PAGE_SIZE,
        ),
      ),
    staleTime: 60_000,
  })

export const partialQuery = (id: string) =>
  queryOptions({
    queryKey: partialKeys.detail(id),
    queryFn: () => unwrap(api.GET('/admin/v1/partials/{id}', { params: { path: { id } } })),
  })

/** Templates whose active or draft versions include a partial key (GET /partials/usage). */
export const partialUsageQuery = (key: string, app: string | null | undefined) =>
  queryOptions({
    queryKey: [...partialKeys.all, 'usage', key, app ?? 'shared'] as const,
    queryFn: () =>
      unwrap(
        api.GET('/admin/v1/partials/usage', {
          params: { query: { key, app: app ?? 'shared' } },
        }),
      ),
    staleTime: 30_000,
  })

export function useCreatePartial() {
  return useMutation({
    mutationFn: (body: CreatePartial) => unwrap(api.POST('/admin/v1/partials', { body })),
    meta: {
      invalidates: [partialKeys.all],
      successMessage: 'Partial created',
      errorToast: false,
    },
  })
}

export function useUpdatePartial(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: UpdatePartial) =>
      unwrap(api.PATCH('/admin/v1/partials/{id}', { params: { path: { id } }, body })),
    // The response has no `open_proposals`; keep the cached ones until the refetch.
    onSuccess: (p) =>
      qc.setQueryData<PartialDetail>(partialKeys.detail(id), (old) =>
        old ? { ...old, ...p } : undefined,
      ),
    // Template previews render with partials, so refresh those too. A save
    // can apply a proposal (`proposal_id`).
    meta: {
      invalidates: [partialKeys.all, [...queryKeys.templates.all, 'render'], proposalKeys.all],
      errorToast: false,
    },
  })
}

/**
 * Delete a partial. Like useDeleteTemplate, success refreshes only lists so the
 * still-mounted editor doesn't refetch a 404. Navigate away, then `forget(id)`.
 */
export function useDeletePartial() {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE('/admin/v1/partials/{id}', { params: { path: { id } } })),
    meta: {
      invalidates: [
        [...partialKeys.all, 'list'],
        [...partialKeys.all, 'keys'],
      ],
      successMessage: 'Partial deleted',
    },
  })
  const forget = (id: string) => {
    qc.removeQueries({ queryKey: partialKeys.detail(id) })
    // Template previews render with partials.
    return qc.invalidateQueries({ queryKey: [...queryKeys.templates.all, 'render'] })
  }
  return { ...mutation, forget }
}
